/**
 * M5T Chat Workflow Web Component
 * Template-rendered bubble (left-aligned, wider than a prose response)
 *
 * The trusted half of the canvas. <m5t-chat-response> renders model text with
 * markdown-it at `html: false` and can never emit markup; this element renders
 * template-authored HTML verbatim and instantiates web components. The trust
 * boundary is the element type, not a flag - so there is no configuration
 * mistake that lets model output cross it.
 *
 * Content arrives as a property, not an attribute, because a rendered view is
 * far too large to live in one and would need double-escaping to survive there:
 *
 *   const el = document.createElement('m5t-chat-workflow');
 *   el.setContent(html, { templateId: 'HCP_SPEND_TABLE' });
 *
 * INTERACTION
 *
 * Anything inside the rendered markup carrying `data-n55-prompt` fires that text
 * as a new conversation turn when clicked:
 *
 *   <a href="#" data-n55-prompt="Break Q3 down by territory">drill down</a>
 *
 * One delegated listener on the shadow root handles all of them, so a re-render
 * cannot strand a handler and templates never attach listeners of their own.
 * Delegating *inside* the shadow root also avoids event retargeting - a document
 * level listener would see this host element as the target, never the anchor.
 *
 * The click leaves as a `workflow-action` CustomEvent carrying `pubsubTopic`,
 * which the app's web-component event bridge forwards to PubSub. This element
 * never imports PubSub - `shared/` sits outside the pnpm workspaces, and the
 * bridge already exists for exactly this.
 *
 * Attributes:
 * - timestamp: string - Timestamp to display (optional, defaults to now)
 * - show-timestamp: boolean - Whether to show timestamp (default: true)
 * - template-id: string - Registered template that produced this view
 * - phase: string - 'end' | 'error' from the turn envelope; styles the error state
 */

/** Default topic a template link publishes on: prompt the agent, echo to canvas. */
const DEFAULT_PROMPT_TOPIC = 'CONVERSATION.PROMPT.AGENT_CANVAS';

const STYLES = `
  :host {
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    max-width: 92%;
    animation: slideIn 0.3s ease-out;
  }

  /* A rendered template owns its own width. Tables, quilts and card grids need
     the whole canvas, and an <ether5-grid> in particular derives its cell size
     from the container's inline size - constraining the bubble here would shrink
     every card in it.
     Scoped to [template-id], which setContent() reflects only once real content
     is installed, so an empty bubble keeps the narrow default. */
  :host([template-id]) {
    align-self: stretch;
    max-width: none;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .bubble-content {
    background: #242424;
    color: #e4e4e4;
    border: 1px solid #3a3a3a;
    border-left: 3px solid #edba08;
    border-radius: 4px 18px 18px 4px;
    padding: 1rem 1.25rem;
    font-size: 0.95rem;
    line-height: 1.5;
    word-wrap: break-word;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  /* A view that failed to hydrate - phase comes from the conversation envelope */
  :host([phase="error"]) .bubble-content {
    border-color: #DD4124;
    border-left-color: #DD4124;
    background: #2d2020;
  }

  .bubble-content h1,
  .bubble-content h2,
  .bubble-content h3 {
    margin: 0.5em 0 0.25em 0;
    color: #edba08;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .bubble-content h1 { font-size: 1.2em; }
  .bubble-content h2 { font-size: 1.1em; }
  .bubble-content h3 { font-size: 1em; }

  .bubble-content p { margin: 0.5em 0; }
  .bubble-content p:first-child { margin-top: 0; }
  .bubble-content p:last-child { margin-bottom: 0; }

  /* Tables are the common case - let them scroll rather than blow out the bubble */
  .bubble-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75em 0;
    font-size: 0.9em;
  }

  .bubble-content th {
    text-align: left;
    text-transform: uppercase;
    font-size: 0.75em;
    letter-spacing: 0.5px;
    color: #a8a8a8;
    border-bottom: 1px solid #3a3a3a;
    padding: 0.5em 0.75em;
  }

  .bubble-content td {
    padding: 0.5em 0.75em;
    border-bottom: 1px solid #2d2d2d;
  }

  .bubble-content tr:hover td { background: #2d2d2d; }

  .table-scroll { overflow-x: auto; }

  /* Install snippets and other emitted code */
  .bubble-content pre {
    background: #1a1a1a;
    border: 1px solid #3a3a3a;
    border-radius: 4px;
    padding: 0.75em 1em;
    margin: 0.5em 0;
    overflow-x: auto;
    font-family: 'Courier New', monospace;
    font-size: 0.82em;
    line-height: 1.5;
    color: #92a8d1;
    white-space: pre-wrap;
    word-break: break-all;
  }

  .bubble-content [data-n55-copy] {
    cursor: pointer;
    background: #2d2d2d;
    color: #e4e4e4;
    border: 1px solid #3a3a3a;
    border-radius: 4px;
    padding: 0.35em 0.8em;
    font: inherit;
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
  }

  .bubble-content [data-n55-copy]:hover {
    border-color: #edba08;
    color: #edba08;
  }

  .bubble-content code {
    background: #1a1a1a;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #92a8d1;
  }

  .bubble-content ul,
  .bubble-content ol { margin: 0.5em 0; padding-left: 1.5em; }

  .bubble-content li { margin: 0.25em 0; }

  .bubble-content a { color: #92a8d1; text-decoration: underline; }
  .bubble-content a:hover { color: #edba08; }

  /* Conversational affordance - a link that asks the next question */
  .bubble-content [data-n55-prompt] {
    cursor: pointer;
    color: #92a8d1;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.3s ease;
  }

  .bubble-content [data-n55-prompt]:hover { color: #edba08; }

  .bubble-content [data-n55-prompt]:focus-visible {
    outline: 2px solid #edba08;
    outline-offset: 2px;
    border-radius: 2px;
  }

  .bubble-timestamp {
    font-size: 0.75rem;
    color: #a8a8a8;
    margin-top: 0.25rem;
    padding: 0 0.5rem;
    text-align: left;
  }

  .bubble-timestamp[hidden] { display: none; }

  @media (max-width: 768px) {
    :host { max-width: 100%; }
    .bubble-content { padding: 0.75rem 1rem; font-size: 0.9rem; }
  }
`;

class M5TChatWorkflow extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._built = false;
    this._html = '';
    this._meta = {};
  }

  static get observedAttributes() {
    return ['timestamp', 'show-timestamp'];
  }

  connectedCallback() {
    this.buildShell();
    this.paint();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this._built) return;
    this.paintTimestamp();
  }

  /**
   * Install rendered template output. Trusted - see the file header.
   *
   * @param {string} html
   * @param {object} [meta] - { templateId, ... }, echoed on every action event
   */
  setContent(html, meta = {}) {
    this._html = String(html ?? '');
    this._meta = meta ?? {};
    if (meta.templateId) this.setAttribute('template-id', meta.templateId);
    this.paint();
  }

  getTimestamp() {
    return this.getAttribute('timestamp') ||
      new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  shouldShowTimestamp() {
    return this.getAttribute('show-timestamp') !== 'false';
  }

  buildShell() {
    if (this._built) return;

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div>
        <div class="bubble-content"></div>
        <div class="bubble-timestamp"></div>
      </div>
    `;

    this._content = this.shadowRoot.querySelector('.bubble-content');
    this._timestamp = this.shadowRoot.querySelector('.bubble-timestamp');
    this._built = true;

    // Bound to the shadow root itself, which buildShell never replaces, so the
    // handler survives every content swap. Registered once per element.
    this.shadowRoot.addEventListener('click', this.handleAction);
    this.shadowRoot.addEventListener('keydown', this.handleKey);

    this.paintTimestamp();
  }

  paintTimestamp() {
    if (!this._timestamp) return;
    this._timestamp.textContent = this.getTimestamp();
    this._timestamp.hidden = !this.shouldShowTimestamp();
  }

  paint() {
    if (!this._built || !this._content) return;

    // Trusted markup, and the parser upgrades any custom elements it contains.
    this._content.innerHTML = this._html;

    // Make affordances reachable without a mouse. Templates author plain
    // anchors; the roles are applied here so no template has to remember.
    for (const el of this._content.querySelectorAll('[data-n55-prompt]')) {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      if (!el.hasAttribute('role') && el.tagName !== 'A' && el.tagName !== 'BUTTON') {
        el.setAttribute('role', 'button');
      }
    }
  }

  /** Enter/Space on a focused affordance behaves like a click. */
  handleKey = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const el = e.target?.closest?.('[data-n55-prompt]');
    if (!el) return;
    e.preventDefault();
    this.fireAction(el);
  };

  handleAction = (e) => {
    // Inside our own shadow root the target is not retargeted, so this is the
    // real anchor rather than the host element.
    const copy = e.target?.closest?.('[data-n55-copy]');
    if (copy) {
      e.preventDefault();
      this.copyFrom(copy);
      return;
    }

    const el = e.target?.closest?.('[data-n55-prompt]');
    if (!el) return;
    e.preventDefault();
    this.fireAction(el);
  };

  /**
   * Copy the text of the element named by `data-n55-copy` (an id within this
   * bubble's shadow root).
   *
   * Reads textContent rather than the attribute so the copied text is exactly
   * what the user sees, entity-decoded by the parser - an escaped `&lt;script&gt;`
   * copies as `<script>`, which is the entire point for an install snippet.
   */
  async copyFrom(trigger) {
    const source = this.shadowRoot.getElementById(trigger.getAttribute('data-n55-copy'));
    if (!source) return;

    const label = trigger.textContent;

    try {
      await navigator.clipboard.writeText(source.textContent);
      trigger.textContent = 'Copied';
    } catch {
      // Clipboard needs a secure context and permission; select the text instead
      // so the user can copy it by hand rather than silently getting nothing.
      const range = document.createRange();
      range.selectNodeContents(source);
      const sel = this.shadowRoot.getSelection?.() ?? window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      trigger.textContent = 'Press Ctrl/Cmd-C';
    }

    setTimeout(() => {
      if (this.isConnected) trigger.textContent = label;
    }, 2000);
  }

  /**
   * Publish the affordance as a conversation turn, via the app's web-component
   * event bridge. `source` stays 'user' - a click is a genuine user action - but
   * `meta.origin` records that it came from a rendered view rather than typing,
   * so a transcript or a VCR tape can reconstruct the daisy chain.
   */
  fireAction(el) {
    const text = el.getAttribute('data-n55-prompt');
    if (!text) return;

    this.dispatchEvent(new CustomEvent('workflow-action', {
      bubbles: true,
      composed: true, // required: the event must cross the shadow boundary
      detail: {
        pubsubTopic: el.getAttribute('data-n55-topic') || DEFAULT_PROMPT_TOPIC,
        message: text,
        source: 'user',
        meta: {
          origin: 'template-link',
          templateId: this._meta.templateId ?? this.getAttribute('template-id') ?? null
        }
      }
    }));
  }
}

customElements.define('m5t-chat-workflow', M5TChatWorkflow);

export default M5TChatWorkflow;
